import { type JudgedRule, JUDGED_PANEL_MAX, JUDGED_RULES } from "./judged-rules";
import { formatDecimal } from "./marks";
import { rankByValue } from "./ranking";
import type { JudgedResultDetail } from "./results";
import type { CompetitorType, EventGender, IrmCode, MeetOutcome, PhaseType } from "./vocab";

/**
 * JUDGED scoring — ONE implementation of every judged sport's formula, shared
 * by OVR (the judged screen computes the parts, the total, the ranks and a
 * bout's outcome while the operator types) and the API (the public feed's
 * across-meets standing, figure skating short + free). Pure and unit-tested
 * (`apps/api/tests/unit/judged-scoring.test.ts`).
 *
 * What the operator types, per sport, is what the old OVR typed
 * (WSL-OVR-X `meets/subjective/*`, docs/ovr-port/spec-judged.md); sports the
 * old OVR never had take the closest analogue the spec names. Every number
 * marked "CONFIRM" there is listed for the TDs. The old OVR had NO tie-break
 * in any judged sport (ties were the array order); every rule here has one,
 * and what is still level after it is the operator's `detail.tie_order`.
 *
 * Gymnastics (GYM) is deliberately MANUAL: its formula waits for the new TSR
 * (user, 2026-10-29) — the operator types the total, stored parts stay as they are.
 *
 * Storage: the JUDGED result detail (`results.ts`) — `parts[]` with the
 * seats' marks (`judges`, `dropped` on a trimmed mark), the typed and the
 * computed `components`, `difficulty`, `penalty` and the part `total`. The
 * apparatus / dance / segment is a VALUE (`code`), never a key.
 */

export { JUDGED_RULES, type JudgedRule };

type JudgedPart = NonNullable<JudgedResultDetail["parts"]>[number];
type JudgedSeatMark = NonNullable<JudgedPart["judges"]>[number];
export type { JudgedPart as JudgedPartResult };

// ─── Which rule ──────────────────────────────────────────────────────────────

export interface JudgedRuleContext {
	sport: string | null | undefined;
	discipline: string | null | undefined;
	/** The event's code ("MKIND", "WSENI…"): its gender letter is ignored. */
	event: string | null | undefined;
	/** Slots in the meet: 2 = a bout / battle (head-to-head rules), else a ranking round. */
	slots: number;
	phaseType?: PhaseType | null | undefined;
	competitorType?: CompetitorType | null | undefined;
	/** `config.scoring.rule` — the meet names a rule itself. */
	override?: string | null | undefined;
}

const isRule = (v: unknown): v is JudgedRule => typeof v === "string" && (JUDGED_RULES as readonly string[]).includes(v);

/** The event code without its leading gender letter ("MDSGS" → "DSGS"). */
export function judgedEventKey(eventCode: string | null | undefined): string {
	const c = (eventCode ?? "").trim().toUpperCase();
	return /^[MWXO]/.test(c) ? c.slice(1) : c;
}

const up = (v: string | null | undefined) => (v ?? "").trim().toUpperCase();

/** Wushu's two-routine events (`programme.data.ts`): the routines, by event key. */
const TAOLU_PAIRS: Readonly<Record<string, readonly [string, string]>> = { DSGS: ["DS", "GS"], NDNG: ["ND", "NG"], JSQS: ["JS", "QS"] };
const TAOLU_ROUTINES: Readonly<Record<string, string>> = {
	CQ: "Changquan",
	NQ: "Nanquan",
	TJQ: "Taijiquan",
	TJJ: "Taijijian",
	DS: "Daoshu",
	GS: "Gunshu",
	ND: "Nandao",
	NG: "Nangun",
	JS: "Jianshu",
	QS: "Qiangshu",
};

/**
 * The rule a meet is scored by — from the codes, never a name (the CLAUDE.md
 * rule "a meet's timed sport is its codes"). `override` (the meet's
 * `config.scoring.rule`) wins when it is a known rule.
 */
export function judgedRuleOf(ctx: JudgedRuleContext): JudgedRule {
	if (isRule(ctx.override)) return ctx.override;
	const sport = up(ctx.sport);
	const disc = up(ctx.discipline);
	const key = judgedEventKey(ctx.event);
	const bout = ctx.slots === 2;
	switch (sport) {
		case "KTE":
			return bout ? "KATA_FLAGS" : "KATA_POINTS";
		case "SIL":
			return "SILAT_SENI";
		case "WSH":
			return TAOLU_PAIRS[key] ? "TAOLU_COMBINED" : "TAOLU";
		case "TKD":
			return key === "PFREE" ? "POOMSAE_FREESTYLE" : "POOMSAE";
		case "DSP":
			if (disc === "BRK") return bout ? "BREAKING_BATTLE" : "MANUAL";
			return ctx.phaseType === "FINAL" ? "DANCE_SKATING" : "DANCE_RECALLS";
		case "JUD":
			return "JUDO_KATA";
		case "MUA":
			return "WAI_KRU";
		case "EQU":
			return ctx.competitorType === "TEAM" ? "DRESSAGE_TEAM" : "DRESSAGE";
		case "SKT":
			return "FIGURE_SKATING";
		case "WSK":
			return key === "WAKE" ? "WAKEBOARD" : "MANUAL";
		default:
			// GYM (postponed until the new TSR), AQ diving / artistic swimming (out of scope), anything new.
			return "MANUAL";
	}
}

// ─── The rule's sheet ────────────────────────────────────────────────────────

/** A number the operator types: its bounds and decimals (more decimals are refused, never rounded). */
export interface JudgedField {
	code: string;
	min: number;
	max: number;
	decimals: number;
}

/** One group of seats on the panel: J1…J6, AC1…AC7, the dressage letters. */
export interface JudgedSeatGroup extends Omit<JudgedField, "code"> {
	/** The group's letters as the sheet prints them ("J", "AC", "T"). Fixed seats (dressage E H C M B) have none. */
	key: string;
	seats: readonly string[];
	/** The component the group's (trimmed) result is stored as; null when the rule reads the marks itself. */
	component: string | null;
	/** Marks dropped from EACH end for `n` marks present. */
	trim: (n: number) => number;
}

export interface JudgedPartDef {
	code: string;
	label: string;
}

export interface JudgedRuleSpec {
	rule: JudgedRule;
	/** The parts each row scores, in order. */
	parts: readonly JudgedPartDef[];
	/** The operator types the part's name (a kata, a poomsae form) — stored as the part's `label`. */
	labelled: boolean;
	groups: readonly JudgedSeatGroup[];
	/** A typed difficulty (figure skating's TES). */
	difficulty: JudgedField | null;
	/** Components the operator types, in sheet order. */
	typed: readonly JudgedField[];
	/** Components the rule computes, in sheet order. */
	computed: readonly string[];
	penalty: JudgedField | null;
	/** The part total itself is typed (a routine's score, a rider's %). */
	typedTotal: JudgedField | null;
	/** Decimals of a part total and of the result. */
	decimals: number;
	lowerIsBetter: boolean;
	/** Needs exactly two slots: the marks of one side are read against the other's. */
	headToHead: boolean;
	/** The result is the sum of parts that may be scored in different meets (short + free). */
	acrossMeets: boolean;
	/** How the result reads ("%" for dressage); null = points. */
	unit: "%" | null;
	/** DRESSAGE_TEAM: the best N parts count, the rest are `dropped`. */
	counting: number | null;
}

export interface JudgedSpecContext {
	event?: string | null | undefined;
	gender?: EventGender | null | undefined;
	/** `config.scoring.judges`. */
	judges?: number | null | undefined;
	/** `config.scoring.rounds` — battle rounds. */
	rounds?: number | null | undefined;
	/** `config.scoring.counting`. */
	counting?: number | null | undefined;
	/** `config.apparatus` — the parts this meet scores. */
	apparatus?: readonly string[] | null | undefined;
	/** A team's roster size (team dressage riders). */
	teamSize?: number | null | undefined;
	/** The meet's phase: a kata round robin (GROUP) seats 7 judges, as the old OVR did. */
	phaseType?: PhaseType | null | undefined;
}

const noTrim = () => 0;
/** Drop one high and one low with five marks or more (IJF kata, IFMA, IWWF, WT poomsae — CONFIRM for 5 judges). */
const highLowFrom5 = (n: number) => (n >= 5 ? 1 : 0);
/** Old OVR silat seni: min(2, ⌊(n − 1) / 2⌋) — 6 marks keep the middle 2. */
const silatTrim = (n: number) => Math.min(2, Math.floor((n - 1) / 2));
/** WKF kata: 7 judges drop 2 + 2, 5 judges 1 + 1. */
const kataTrim = (n: number) => (n >= 7 ? 2 : n >= 5 ? 1 : 0);

const LETTERS = "ABCDEFGHIJKLMNO".split("");
const DRESSAGE_SEATS = ["E", "H", "C", "M", "B"] as const;

const numbered = (prefix: string, n: number) => Array.from({ length: n }, (_, i) => `${prefix}${i + 1}`);
const clampSeats = (n: number | null | undefined, fallback: number, max = JUDGED_PANEL_MAX) => Math.max(1, Math.min(max, n && Number.isInteger(n) ? n : fallback));

function group(key: string, count: number, component: string | null, field: Omit<JudgedField, "code">, trim: (n: number) => number): JudgedSeatGroup {
	return { key, seats: numbered(key, count), component, trim, ...field };
}

const field = (code: string, min: number, max: number, decimals: number): JudgedField => ({ code, min, max, decimals });

const DANCES: Readonly<Record<string, readonly string[]>> = {
	STWAL: ["W"],
	STTAN: ["T"],
	STQS: ["Q"],
	ST5: ["W", "T", "V", "F", "Q"],
	LACHA: ["C"],
	LARUM: ["R"],
	LAJIV: ["J"],
	LA5: ["S", "C", "R", "P", "J"],
};
const DANCE_NAMES: Readonly<Record<string, string>> = {
	W: "Waltz",
	T: "Tango",
	V: "Viennese Waltz",
	F: "Slow Foxtrot",
	Q: "Quickstep",
	S: "Samba",
	C: "Cha-Cha-Cha",
	R: "Rumba",
	P: "Paso Doble",
	J: "Jive",
};
const DRESSAGE_TESTS: Readonly<Record<string, string>> = { GP: "Grand Prix", GPS: "Grand Prix Special", GPFS: "Grand Prix Freestyle" };
const SKATING_SEGMENTS: Readonly<Record<string, string>> = { SP: "Short program", FS: "Free skating" };

/** ISU singles programme component factors (2024–25): [short, free] by gender. CONFIRM with the TD. */
export const SKATING_PCS_FACTORS = { MEN: [1.67, 3.33], WOMEN: [1.33, 2.67] } as const;

/** The meet's own part list (`config.apparatus`) wins over the rule's default, keeping the rule's names. */
function partsFrom(defaults: readonly JudgedPartDef[], apparatus: readonly string[] | null | undefined, names: Readonly<Record<string, string>> = {}): JudgedPartDef[] {
	if (!apparatus || apparatus.length === 0) return [...defaults];
	return apparatus.map((code) => ({ code, label: defaults.find((d) => d.code === code)?.label ?? names[code] ?? code }));
}

const BASE: Omit<JudgedRuleSpec, "rule" | "parts"> = {
	labelled: false,
	groups: [],
	difficulty: null,
	typed: [],
	computed: [],
	penalty: null,
	typedTotal: null,
	decimals: 2,
	lowerIsBetter: false,
	headToHead: false,
	acrossMeets: false,
	unit: null,
	counting: null,
};

/** A rule's sheet for one meet: its parts, panel, typed fields and decimals. */
export function judgedSpec(rule: JudgedRule, ctx: JudgedSpecContext = {}): JudgedRuleSpec {
	const key = judgedEventKey(ctx.event);
	const judges = (fallback: number) => clampSeats(ctx.judges, fallback);
	switch (rule) {
		case "SILAT_SENI":
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "SENI", label: "Seni" }], ctx.apparatus),
				groups: [group("J", judges(6), "median", { min: 0, max: 10, decimals: 3 }, silatTrim)],
				computed: ["median", "standard_deviation"],
				penalty: field("penalty", 0, 10, 3),
				decimals: 3,
			};
		case "TAOLU": {
			const name = TAOLU_ROUTINES[key] ?? "Routine";
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: key || "R1", label: name }], ctx.apparatus, TAOLU_ROUTINES),
				typed: [field("a_quality", 0, 5, 2), field("b_performance", 0, 3, 3), field("c_difficulty", 0, 2, 2), field("bonus", 0, 5, 2)],
				penalty: field("penalty", 0, 10, 2),
				decimals: 3,
			};
		}
		case "TAOLU_COMBINED": {
			const pair = TAOLU_PAIRS[key] ?? ["R1", "R2"];
			return {
				...BASE,
				rule,
				parts: partsFrom(
					pair.map((code) => ({ code, label: TAOLU_ROUTINES[code] ?? code })),
					ctx.apparatus,
					TAOLU_ROUTINES,
				),
				typedTotal: field("total", 0, 20, 3),
				decimals: 3,
				acrossMeets: true,
			};
		}
		case "POOMSAE": {
			const n = judges(7);
			return {
				...BASE,
				rule,
				parts: partsFrom(
					[
						{ code: "P1", label: "Poomsae 1" },
						{ code: "P2", label: "Poomsae 2" },
					],
					ctx.apparatus,
				),
				labelled: true,
				groups: [group("AC", n, "accuracy", { min: 0, max: 4, decimals: 1 }, highLowFrom5), group("PR", n, "presentation", { min: 0, max: 6, decimals: 1 }, highLowFrom5)],
				computed: ["accuracy", "presentation"],
				penalty: field("penalty", 0, 10, 1),
				decimals: 2,
			};
		}
		case "POOMSAE_FREESTYLE": {
			const n = judges(7);
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "F1", label: "Freestyle" }], ctx.apparatus),
				groups: [
					group("TS", n, "technical_skills", { min: 0, max: 6, decimals: 1 }, highLowFrom5),
					group("PR", n, "presentation", { min: 0, max: 4, decimals: 1 }, highLowFrom5),
				],
				computed: ["technical_skills", "presentation"],
				penalty: field("penalty", 0, 10, 1),
				decimals: 2,
			};
		}
		case "KATA_POINTS": {
			const n = judges(7);
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "K1", label: "Kata" }], ctx.apparatus),
				labelled: true,
				groups: [group("T", n, "technical", { min: 0, max: 10, decimals: 1 }, kataTrim), group("A", n, "athletic", { min: 0, max: 10, decimals: 1 }, kataTrim)],
				computed: ["technical", "athletic"],
				decimals: 2,
			};
		}
		case "KATA_FLAGS":
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "K1", label: "Kata" }], ctx.apparatus),
				labelled: true,
				// The old OVR: J1…J7 in a round-robin ("round") stage, J1…J5 in an elimination bout.
				groups: [group("J", judges(ctx.phaseType === "GROUP" || ctx.phaseType === "ROUND" || ctx.phaseType === "RANKING" ? 7 : 5), null, { min: 0, max: 10, decimals: 2 }, noTrim)],
				computed: ["votes"],
				decimals: 0,
				headToHead: true,
			};
		case "BREAKING_BATTLE": {
			const rounds = clampSeats(ctx.rounds, 3, 5);
			return {
				...BASE,
				rule,
				parts: partsFrom(
					Array.from({ length: rounds }, (_, i) => ({ code: `R${i + 1}`, label: `Round ${i + 1}` })),
					ctx.apparatus,
				),
				groups: [group("J", judges(9), null, { min: 0, max: 1, decimals: 0 }, noTrim)],
				computed: ["votes", "won"],
				decimals: 0,
				headToHead: true,
			};
		}
		case "JUDO_KATA":
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "KATA", label: "Kata" }], ctx.apparatus),
				groups: [group("J", judges(5), null, { min: 0, max: 1000, decimals: 1 }, highLowFrom5)],
				typed: [field("forgotten", 0, 50, 0), field("big_mistakes", 0, 50, 0)],
				decimals: 1,
			};
		case "WAI_KRU":
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "WK", label: "Wai kru" }], ctx.apparatus),
				groups: [group("J", judges(5), "mean", { min: 0, max: 10, decimals: 2 }, highLowFrom5)],
				computed: ["mean"],
				decimals: 2,
			};
		case "DRESSAGE": {
			const n = clampSeats(ctx.judges, 5, DRESSAGE_SEATS.length);
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "GP", label: "Grand Prix" }], ctx.apparatus, DRESSAGE_TESTS),
				groups: [{ key: "", seats: DRESSAGE_SEATS.slice(0, n), component: null, min: 0, max: 100, decimals: 3, trim: noTrim }],
				typed: [field("collective", 0, 1000, 1)],
				decimals: 3,
				unit: "%",
			};
		}
		case "DRESSAGE_TEAM": {
			const riders = clampSeats(ctx.teamSize, 4, 6);
			return {
				...BASE,
				rule,
				parts: partsFrom(
					Array.from({ length: riders }, (_, i) => ({ code: `R${i + 1}`, label: `Rider ${i + 1}` })),
					ctx.apparatus,
				),
				labelled: true,
				typedTotal: field("total", 0, 100, 3),
				decimals: 3,
				counting: clampSeats(ctx.counting, 3, riders),
			};
		}
		case "FIGURE_SKATING":
			return {
				...BASE,
				rule,
				parts: partsFrom(
					[
						{ code: "SP", label: "Short program" },
						{ code: "FS", label: "Free skating" },
					],
					ctx.apparatus,
					SKATING_SEGMENTS,
				),
				difficulty: field("tes", 0, 300, 2),
				typed: [field("composition", 0, 10, 2), field("presentation", 0, 10, 2), field("skating_skills", 0, 10, 2)],
				computed: ["pcs"],
				penalty: field("penalty", 0, 30, 1),
				decimals: 2,
				acrossMeets: true,
			};
		case "WAKEBOARD":
			return {
				...BASE,
				rule,
				parts: partsFrom([{ code: "R1", label: "Run" }], ctx.apparatus),
				groups: [group("J", judges(3), "mean", { min: 0, max: 100, decimals: 1 }, highLowFrom5)],
				typed: [field("execution", 0, 100, 1)],
				computed: ["mean"],
				decimals: 2,
			};
		case "DANCE_SKATING":
		case "DANCE_RECALLS": {
			const n = clampSeats(ctx.judges, 7);
			const final = rule === "DANCE_SKATING";
			return {
				...BASE,
				rule,
				parts: partsFrom(
					(DANCES[key] ?? ["D1"]).map((code) => ({ code, label: DANCE_NAMES[code] ?? code })),
					ctx.apparatus,
					DANCE_NAMES,
				),
				groups: [{ key: "", seats: LETTERS.slice(0, n), component: null, min: final ? 1 : 0, max: final ? 24 : 1, decimals: 0, trim: noTrim }],
				computed: final ? [] : ["crosses"],
				decimals: final ? 1 : 0,
				lowerIsBetter: final,
			};
		}
		case "MANUAL":
			return { ...BASE, rule, parts: partsFrom([], ctx.apparatus), decimals: 3 };
	}
}

// ─── Arithmetic ──────────────────────────────────────────────────────────────

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
/** Half away from zero at `d` decimals, without float drift (1.005 → 1.01): shifted through the decimal exponent. */
const round = (v: number, d: number): number => {
	const shifted = Number(`${Math.abs(v).toFixed(d + 3)}e${d}`);
	const r = Number(`${Math.round(shifted)}e-${d}`) * Math.sign(v);
	return r === 0 ? 0 : r;
};
const sum = (xs: readonly number[]) => xs.reduce((t, x) => t + x, 0);
const mean = (xs: readonly number[]) => (xs.length === 0 ? 0 : sum(xs) / xs.length);

/**
 * The marks a trimmed panel keeps: `drop` taken off EACH end. Equal marks are
 * dropped by seat order (the first high, the last low), so the struck seats
 * are stable.
 */
export function trimMarks(values: readonly number[], drop: number): { kept: number[]; dropped: Set<number> } {
	const d = Math.max(0, Math.min(drop, Math.floor((values.length - 1) / 2)));
	const order = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v || a.i - b.i);
	const dropped = new Set<number>([...order.slice(0, d), ...order.slice(order.length - d)].map((x) => x.i));
	return { kept: values.filter((_, i) => !dropped.has(i)), dropped };
}

/** Population standard deviation (the old OVR's silat SD: over ALL marks, the dropped ones too). */
export function populationSd(values: readonly number[]): number {
	if (values.length === 0) return 0;
	const m = mean(values);
	return Math.sqrt(mean(values.map((v) => (v - m) ** 2)));
}

// ─── Reading what is stored ──────────────────────────────────────────────────

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const list = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isObj) : []);

/** What the operator has typed into one part, read from a stored part (computed values are ignored). */
export interface JudgedPartInput {
	label: string | null;
	/** Seat → mark. */
	marks: Map<string, number>;
	/** Typed component code → value. */
	typed: Map<string, number>;
	difficulty: number | null;
	penalty: number | null;
	total: number | null;
}

export const emptyPartInput = (): JudgedPartInput => ({ label: null, marks: new Map(), typed: new Map(), difficulty: null, penalty: null, total: null });

/** The inputs of every part in a stored detail, by part code. */
export function readJudgedInputs(spec: JudgedRuleSpec, detail: unknown): Map<string, JudgedPartInput> {
	const out = new Map<string, JudgedPartInput>();
	const typedCodes = new Set(spec.typed.map((f) => f.code));
	for (const p of list(isObj(detail) ? detail.parts : undefined)) {
		if (typeof p.code !== "string") continue;
		const input = emptyPartInput();
		if (typeof p.label === "string" && p.label.trim() !== "") input.label = p.label;
		for (const j of list(p.judges)) if (typeof j.seat === "string" && isNum(j.value)) input.marks.set(j.seat, j.value);
		for (const c of list(p.components)) if (typeof c.code === "string" && typedCodes.has(c.code) && isNum(c.value)) input.typed.set(c.code, c.value);
		if (spec.difficulty && isNum(p.difficulty)) input.difficulty = p.difficulty;
		if (spec.penalty && isNum(p.penalty)) input.penalty = p.penalty;
		if (spec.typedTotal && isNum(p.total)) input.total = p.total;
		out.set(p.code, input);
	}
	return out;
}

/** The part has a number in it (a label alone does not score). */
function scored(input: JudgedPartInput): boolean {
	return input.marks.size > 0 || input.typed.size > 0 || input.difficulty !== null || input.penalty !== null || input.total !== null;
}

/** The part's seat marks in panel order, with the seat names. */
function marksOf(g: JudgedSeatGroup, input: JudgedPartInput): { seat: string; value: number }[] {
	return g.seats.flatMap((seat) => {
		const v = input.marks.get(seat);
		return v === undefined ? [] : [{ seat, value: v }];
	});
}

// ─── One part ────────────────────────────────────────────────────────────────

/** A computed part and the numbers its row's tie-break reads. */
export interface ComputedPart {
	part: JudgedPart;
	scored: boolean;
	/** Every mark of the panel, the dropped ones too (the "total of all marks" tie-breaks). */
	allMarks: number;
}

/** The context a head-to-head part needs: the other side's marks for the same part. */
interface PartCtx {
	gender?: EventGender | null | undefined;
	other?: JudgedPartInput | undefined;
}

function seatList(g: JudgedSeatGroup, input: JudgedPartInput): { judges: JudgedSeatMark[]; kept: number[]; all: number[] } {
	const marks = marksOf(g, input);
	const values = marks.map((m) => m.value);
	const { kept, dropped } = trimMarks(values, g.trim(values.length));
	const judges = marks.map((m, i): JudgedSeatMark => (dropped.has(i) ? { seat: m.seat, value: m.value, dropped: true } : { seat: m.seat, value: m.value }));
	return { judges, kept, all: values };
}

/**
 * One part of one row, by its rule — the formulas (old OVR file:line in the spec):
 *
 *   SILAT_SENI     median = mean of the marks left after dropping min(2, ⌊(n−1)/2⌋) each end;
 *                  SD = population σ of ALL marks; total = max(0, median − foul), 3 dp
 *   TAOLU          total = A + B + C + bonus − deduction, 3 dp
 *   POOMSAE(_FS)   per group the mean of the marks left (high / low dropped with ≥ 5);
 *                  total = Σ group means − penalty, 2 dp
 *   KATA_POINTS    per group the SUM of the marks left (2 + 2 dropped with 7);
 *                  total = 0.7 × technical + 0.3 × athletic, 2 dp
 *   JUDO_KATA      total = Σ of the judges' totals left (high / low dropped with 5), 1 dp
 *   WAI_KRU        total = mean of the marks left, 2 dp
 *   WAKEBOARD      total = mean of the judges' totals left, 2 dp
 *   DRESSAGE       total = mean of the judges' percentages, 3 dp
 *   FIGURE_SKATING PCS = (CO + PR + SK) × factor; total = TES + PCS − deductions, 2 dp
 *   KATA_FLAGS     votes = judges whose mark for this side beats the other side's
 *   BREAKING       votes = judges who voted for this side (1); won = 1 when they outnumber the other's
 *   DANCE_RECALLS  crosses = judges who marked the couple (1)
 *   TAOLU_COMBINED / DRESSAGE_TEAM   the part total is typed
 */
export function computeJudgedPart(spec: JudgedRuleSpec, def: JudgedPartDef, input: JudgedPartInput, ctx: PartCtx = {}): ComputedPart {
	const d = spec.decimals;
	const part: JudgedPart = { code: def.code, total: 0 };
	if (input.label !== null) part.label = input.label;
	const judges: JudgedSeatMark[] = [];
	const comps = new Map<string, number>();
	let allMarks = 0;
	const groupResults = spec.groups.map((g) => {
		const s = seatList(g, input);
		judges.push(...s.judges);
		allMarks += sum(s.all);
		return { g, ...s };
	});
	for (const f of spec.typed) {
		const v = input.typed.get(f.code);
		if (v !== undefined) comps.set(f.code, v);
	}
	const penalty = input.penalty ?? 0;
	let total = 0;
	switch (spec.rule) {
		case "SILAT_SENI": {
			const r = groupResults[0];
			const median = r && r.kept.length ? mean(r.kept) : 0;
			if (r && r.all.length) {
				comps.set("median", round(median, 3));
				comps.set("standard_deviation", round(populationSd(r.all), 3));
			}
			total = Math.max(0, median - penalty);
			break;
		}
		case "TAOLU":
			total = sum(["a_quality", "b_performance", "c_difficulty", "bonus"].map((c) => comps.get(c) ?? 0)) - penalty;
			break;
		case "POOMSAE":
		case "POOMSAE_FREESTYLE":
		case "WAI_KRU":
		case "WAKEBOARD": {
			total = -penalty;
			for (const r of groupResults) {
				const m = mean(r.kept);
				if (r.g.component && r.all.length) comps.set(r.g.component, round(m, 3));
				total += m;
			}
			break;
		}
		case "KATA_POINTS": {
			const [tech, ath] = groupResults.map((r) => sum(r.kept));
			if (groupResults[0]?.all.length) comps.set("technical", round(tech ?? 0, 2));
			if (groupResults[1]?.all.length) comps.set("athletic", round(ath ?? 0, 2));
			total = 0.7 * (tech ?? 0) + 0.3 * (ath ?? 0);
			break;
		}
		case "JUDO_KATA":
			total = sum(groupResults[0]?.kept ?? []);
			break;
		case "DRESSAGE":
			total = mean(groupResults[0]?.kept ?? []);
			break;
		case "FIGURE_SKATING": {
			const factors = SKATING_PCS_FACTORS[ctx.gender === "WOMEN" ? "WOMEN" : "MEN"];
			const factor = def.code === "SP" ? factors[0] : factors[1];
			const pcsMarks = ["composition", "presentation", "skating_skills"].map((c) => comps.get(c));
			const pcs = round(sum(pcsMarks.map((v) => v ?? 0)) * factor, 2);
			if (pcsMarks.some((v) => v !== undefined)) comps.set("pcs", pcs);
			if (input.difficulty !== null) part.difficulty = input.difficulty;
			total = (input.difficulty ?? 0) + pcs - penalty;
			break;
		}
		case "KATA_FLAGS": {
			const mine = groupResults[0]?.g;
			let votes = 0;
			if (mine && ctx.other) {
				for (const seat of mine.seats) {
					const a = input.marks.get(seat);
					const b = ctx.other.marks.get(seat);
					if (a !== undefined && b !== undefined && a > b) votes++;
				}
			}
			if (input.marks.size > 0) comps.set("votes", votes);
			total = votes;
			break;
		}
		case "BREAKING_BATTLE": {
			const votes = sum(groupResults[0]?.all ?? []);
			const otherVotes = ctx.other && groupResults[0] ? sum(marksOf(groupResults[0].g, ctx.other).map((m) => m.value)) : 0;
			if (input.marks.size > 0) {
				comps.set("votes", votes);
				comps.set("won", votes > otherVotes ? 1 : 0);
			}
			total = votes;
			break;
		}
		case "DANCE_RECALLS": {
			const crosses = sum(groupResults[0]?.all ?? []);
			if (input.marks.size > 0) comps.set("crosses", crosses);
			total = crosses;
			break;
		}
		case "DANCE_SKATING":
			// The place is decided across every couple (skatingPlaces); the meet fills it in.
			total = 0;
			break;
		case "TAOLU_COMBINED":
		case "DRESSAGE_TEAM":
		case "MANUAL":
			total = input.total ?? 0;
			break;
	}
	if (judges.length) part.judges = judges;
	// Typed first, then computed, each in the sheet's order.
	const components = [...spec.typed.map((f) => f.code), ...spec.computed].flatMap((code) => {
		const v = comps.get(code);
		return v === undefined ? [] : [{ code, value: v }];
	});
	if (components.length) part.components = components;
	if (spec.penalty && input.penalty !== null) part.penalty = input.penalty;
	part.total = round(total, d);
	return { part, scored: scored(input), allMarks };
}

// ─── Dancesport: the Skating System ──────────────────────────────────────────

/**
 * One dance's places by the Skating System (WDSF Competition Rules, Rules
 * 5–8): each judge places every couple 1…n. The place p goes to the couple
 * with a MAJORITY (> half the judges) of marks "p or better", looking at
 * column p, then p + 1…; several majorities in a column → the larger
 * majority, then the lower sum of those marks, then the next column (Rule 8);
 * still level after the last column → the couples share the places (2.5).
 *
 * `marks`: per couple, the judges' places (a missing mark is not counted).
 */
export function skatingPlaces(marks: ReadonlyMap<string, readonly number[]>, judges: number): Map<string, number> {
	const ids = [...marks.keys()];
	const n = ids.length;
	const majority = Math.floor(judges / 2) + 1;
	const count = (id: string, k: number) => (marks.get(id) ?? []).filter((m) => m <= k).length;
	const sumTo = (id: string, k: number) => sum((marks.get(id) ?? []).filter((m) => m <= k));
	const out = new Map<string, number>();
	let remaining = [...ids];
	let place = 1;
	const lastCol = Math.max(n, ...ids.flatMap((id) => marks.get(id) ?? []));
	while (remaining.length > 0) {
		let k = place;
		let cands: string[] = [];
		while (k <= lastCol) {
			cands = remaining.filter((id) => count(id, k) >= majority);
			if (cands.length) break;
			k++;
		}
		if (cands.length === 0) {
			// No majority anywhere (marks missing): the rest share what is left.
			const shared = place + (remaining.length - 1) / 2;
			for (const id of remaining) out.set(id, shared);
			break;
		}
		const cmp = (a: string, b: string): number => {
			for (let col = k; col <= lastCol; col++) {
				const c = count(b, col) - count(a, col);
				if (c !== 0) return c;
				const s = sumTo(a, col) - sumTo(b, col);
				if (s !== 0) return s;
			}
			return 0;
		};
		const ordered = [...cands].sort(cmp);
		let i = 0;
		while (i < ordered.length) {
			let j = i + 1;
			while (j < ordered.length && cmp(ordered[i]!, ordered[j]!) === 0) j++;
			const shared = place + (j - i - 1) / 2;
			for (let x = i; x < j; x++) out.set(ordered[x]!, shared);
			place += j - i;
			i = j;
		}
		const done = new Set(ordered);
		remaining = remaining.filter((id) => !done.has(id));
	}
	return out;
}

/**
 * The final placing of a multi-dance event (Rules 9–11): the lowest sum of
 * dance places wins. Level sums for place p: Rule 10 — the couple with more
 * dances placed p or better, then the lower sum of those places; Rule 11 —
 * every judge's mark of every dance read as ONE dance (majority over judges ×
 * dances) at column p, p + 1…. Still level: the couples share the place (the
 * operator's `tie_order` decides). Returns a whole place per couple (shared
 * places repeat: 1, 2, 2, 4).
 */
export function skatingFinal(dancePlaces: ReadonlyMap<string, readonly number[]>, allMarks: ReadonlyMap<string, readonly number[]>, judges: number, dances: number): Map<string, number> {
	const total = (id: string) => sum(dancePlaces.get(id) ?? []);
	const ids = [...dancePlaces.keys()].sort((a, b) => total(a) - total(b));
	const out = new Map<string, number>();
	const majority = Math.floor((judges * dances) / 2) + 1;
	const lastCol = Math.max(ids.length, ...ids.flatMap((id) => allMarks.get(id) ?? []));
	let place = 1;
	let i = 0;
	while (i < ids.length) {
		let j = i + 1;
		while (j < ids.length && total(ids[j]!) === total(ids[i]!)) j++;
		let group = ids.slice(i, j);
		while (group.length > 0) {
			if (group.length === 1) {
				out.set(group[0]!, place++);
				break;
			}
			const p = place;
			const r10 = (id: string): [number, number] => {
				const within = (dancePlaces.get(id) ?? []).filter((x) => x <= p);
				return [within.length, sum(within)];
			};
			const best = group.reduce<string[]>((acc, id) => {
				if (acc.length === 0) return [id];
				const [c, s] = r10(id);
				const [bc, bs] = r10(acc[0]!);
				if (c > bc || (c === bc && s < bs)) return [id];
				if (c === bc && s === bs) return [...acc, id];
				return acc;
			}, []);
			let winners = best;
			if (winners.length > 1) {
				// Rule 11: all marks as one dance, column p onwards.
				for (let col = p; col <= lastCol && winners.length > 1; col++) {
					const score = (id: string): [number, number] => {
						const within = (allMarks.get(id) ?? []).filter((x) => x <= col);
						return [within.length >= majority ? within.length : -1, sum(within)];
					};
					let top: string[] = [];
					for (const id of winners) {
						if (top.length === 0) {
							top = [id];
							continue;
						}
						const [c, s] = score(id);
						const [bc, bs] = score(top[0]!);
						if (c > bc || (c === bc && c >= 0 && s < bs)) top = [id];
						else if (c === bc && (c < 0 || s === bs)) top.push(id);
					}
					winners = top;
				}
			}
			if (winners.length > 1) {
				// Still level: only these couples share the place; a couple Rule 10 already put behind them does not.
				for (const id of winners) out.set(id, p);
				place += winners.length;
				const won = new Set(winners);
				group = group.filter((id) => !won.has(id));
				continue;
			}
			out.set(winners[0]!, place++);
			group = group.filter((id) => id !== winners[0]);
		}
		i = j;
	}
	return out;
}

// ─── Bout or ranked round ────────────────────────────────────────────────────

/** Phases of an elimination draw: a sided two-slot meet in one of them is a bout. */
const KNOCKOUT_PHASES: ReadonlySet<PhaseType> = new Set(["R128", "R64", "R32", "R16", "QUARTERFINAL", "SEMIFINAL", "REPECHAGE", "BRONZE"]);
/** Rules whose FINAL is a two-performer bout when the discipline is sided (seni gold bout, a kata points medal bout). */
const FINAL_BOUT_RULES: ReadonlySet<JudgedRule> = new Set(["SILAT_SENI", "KATA_POINTS"]);

/**
 * Is this JUDGED meet a head-to-head bout (a winner and a loser) or a ranked
 * round? THE one answer for OVR (whether to write WIN / LOSS) and the public
 * feed (`unit.kind` / `event.kind`): decided by the RULE, never by "two
 * slots and an outcome" — a two-entrant figure skating, dressage, poomsae or
 * diving final stays ranked, whatever an older writer stored.
 *
 *   · two slots and a bout rule (kata flags, breaking battle); or
 *   · two slots, a sided discipline (AKA / AO, red / blue) and an elimination
 *     phase — plus the FINAL for seni and kata points (their medal bouts).
 */
export function judgedIsBout(rule: JudgedRule, ctx: { slots: number; sided: boolean; phaseType?: PhaseType | null | undefined }): boolean {
	if (ctx.slots !== 2 || rule === "MANUAL") return false;
	if (judgedSpec(rule).headToHead) return true;
	if (!ctx.sided || !ctx.phaseType) return false;
	return KNOCKOUT_PHASES.has(ctx.phaseType) || (ctx.phaseType === "FINAL" && FINAL_BOUT_RULES.has(rule));
}

/** Rounds at `decimals` the way every judged total is rounded (half away from zero, no float drift). */
export const roundJudged = (v: number, decimals: number): number => round(v, decimals);

// ─── A whole meet ────────────────────────────────────────────────────────────

export interface JudgedRowInput {
	id: string;
	irm: IrmCode | null;
	/** The stored (or draft) JUDGED detail. */
	detail: unknown;
	/** MANUAL: the typed total (`result_value`). */
	value?: number | null;
}

export interface JudgedRowResult {
	id: string;
	parts: JudgedPart[];
	/** The result (`result_value`): null while nothing is scored. */
	value: number | null;
	tieBreak: number[];
}

export interface JudgedMeetResult {
	rows: Map<string, JudgedRowResult>;
	ranks: Map<string, number | null>;
	/** A two-slot meet's outcomes (null = not decided); null on any other meet. */
	outcomes: Map<string, MeetOutcome | null> | null;
}

const tieOrderOf = (detail: unknown): number => (isObj(detail) && isNum(detail.tie_order) ? detail.tie_order : Number.POSITIVE_INFINITY);

const comp = (p: JudgedPart | undefined, code: string): number | undefined => p?.components?.find((c) => c.code === code)?.value;
const compSum = (parts: readonly JudgedPart[], code: string): number => sum(parts.map((p) => comp(p, code) ?? 0));
const INF = Number.POSITIVE_INFINITY;
const neg = (v: number | undefined) => (v === undefined ? INF : -v);

/**
 * The row's tie-break vector (SMALLER wins, `rankByValue`'s convention) —
 * the sport's rule, which the old OVR did not have (CONFIRM each with the TD):
 *
 *   SILAT_SENI      lower SD, then the higher median, then the lower foul (PERSILAT)
 *   TAOLU           higher B, then A, then C, then the lower deduction (IWUF)
 *   TAOLU_COMBINED  the higher single routine, then the other
 *   POOMSAE         higher presentation, then the higher total of every mark (WT)
 *   P. FREESTYLE    higher technical skills, then every mark
 *   KATA_POINTS     higher athletic, then technical (WKF)
 *   BREAKING        more votes over the battle
 *   JUDO_KATA       fewer "forgotten", fewer big mistakes, then every mark
 *   WAI_KRU         the higher total of every mark
 *   DRESSAGE        higher collective marks (FEI)
 *   DRESSAGE_TEAM   the higher lowest counted score
 *   FIGURE_SKATING  the higher free skating score (overall), else the higher TES (ISU)
 *   WAKEBOARD       higher execution (IWWF)
 */
export function judgedTieBreak(rule: JudgedRule, parts: readonly JudgedPart[], allMarks = 0): number[] {
	const counted = parts.filter((p) => p.dropped !== true);
	const first = counted[0];
	switch (rule) {
		case "SILAT_SENI":
			return [comp(first, "standard_deviation") ?? INF, neg(comp(first, "median")), first?.penalty ?? 0];
		case "TAOLU":
			return [neg(comp(first, "b_performance")), neg(comp(first, "a_quality")), neg(comp(first, "c_difficulty")), first?.penalty ?? 0];
		case "TAOLU_COMBINED": {
			const totals = counted.map((p) => p.total).sort((a, b) => b - a);
			return [neg(totals[0]), neg(totals[1])];
		}
		case "POOMSAE":
			return [-compSum(counted, "presentation"), -allMarks];
		case "POOMSAE_FREESTYLE":
			return [-compSum(counted, "technical_skills"), -allMarks];
		case "KATA_POINTS":
			return [-compSum(counted, "athletic"), -compSum(counted, "technical")];
		case "BREAKING_BATTLE":
			return [-compSum(counted, "votes")];
		case "JUDO_KATA":
			return [compSum(counted, "forgotten"), compSum(counted, "big_mistakes"), -allMarks];
		case "WAI_KRU":
			return [-allMarks];
		case "DRESSAGE":
			return [neg(comp(first, "collective"))];
		case "DRESSAGE_TEAM":
			return [counted.length ? -Math.min(...counted.map((p) => p.total)) : INF];
		case "FIGURE_SKATING": {
			const fs = counted.find((p) => p.code === "FS");
			if (fs && counted.length > 1) return [-fs.total, neg(fs.difficulty)];
			return [neg(first?.difficulty)];
		}
		case "WAKEBOARD":
			return [neg(comp(first, "execution"))];
		default:
			return [];
	}
}

/**
 * The result of a row's parts (`result_value`), at the rule's decimals:
 * POOMSAE averages its poomsae, BREAKING counts the rounds won, DRESSAGE_TEAM
 * sums its best `counting` riders (marking the rest `dropped`), the rest sum
 * their parts. null when no part is scored.
 */
export function judgedAggregate(spec: JudgedRuleSpec, parts: JudgedPart[], scoredCodes: ReadonlySet<string>): number | null {
	const live = parts.filter((p) => scoredCodes.has(p.code));
	if (live.length === 0) return null;
	const d = spec.decimals;
	switch (spec.rule) {
		case "POOMSAE":
			return round(mean(live.map((p) => p.total)), d);
		case "BREAKING_BATTLE":
			return compSum(live, "won");
		case "DRESSAGE_TEAM": {
			const n = spec.counting ?? live.length;
			const best = [...live].sort((a, b) => b.total - a.total).slice(0, n);
			const keep = new Set(best);
			for (const p of live) {
				if (keep.has(p)) delete p.dropped;
				else p.dropped = true;
			}
			return round(sum(best.map((p) => p.total)), d);
		}
		default:
			return round(sum(live.map((p) => p.total)), d);
	}
}

/** A two-slot meet's outcomes from the ranks: one IRM → the other side wins; better rank wins; level → undecided. */
export function judgedOutcomes(a: { irm: IrmCode | null; rank: number | null }, b: { irm: IrmCode | null; rank: number | null }): [MeetOutcome | null, MeetOutcome | null] {
	if (a.irm !== null && b.irm !== null) return [null, null];
	if (a.irm !== null) return ["LOSS", "WIN"];
	if (b.irm !== null) return ["WIN", "LOSS"];
	if (a.rank === null || b.rank === null || a.rank === b.rank) return [null, null];
	return a.rank < b.rank ? ["WIN", "LOSS"] : ["LOSS", "WIN"];
}

/**
 * Every row of a meet: each part computed, the result, the tie-break, the
 * ranks (`rankByValue` in the rule's direction, the rule's tie-break, then
 * the operator's `tie_order`), and a two-slot meet's outcomes. Rows with an
 * IRM keep their parts but are not ranked.
 */
export function computeJudgedMeet(
	spec: JudgedRuleSpec,
	rows: readonly JudgedRowInput[],
	opts: { gender?: EventGender | null; bout?: boolean } = {},
): JudgedMeetResult {
	const inputs = rows.map((r) => readJudgedInputs(spec, r.detail));
	const out = new Map<string, JudgedRowResult>();
	const twoSlot = rows.length === 2;
	// Only a bout has a winner (`judgedIsBout`): a two-entrant ranked final keeps its ranks and no outcome.
	const bout = twoSlot && (opts.bout ?? spec.headToHead);
	const allMarksOf = new Map<string, number>();

	rows.forEach((r, i) => {
		const mine = inputs[i]!;
		const theirs = twoSlot ? inputs[1 - i] : undefined;
		const scoredCodes = new Set<string>();
		let marks = 0;
		const parts: JudgedPart[] = [];
		for (const def of spec.parts) {
			const input = mine.get(def.code);
			if (!input) continue;
			const c = computeJudgedPart(spec, def, input, { gender: opts.gender, other: theirs?.get(def.code) });
			// A part with nothing in it (no label either) is not stored.
			if (!c.scored && input.label === null) continue;
			if (c.scored) scoredCodes.add(def.code);
			marks += c.allMarks;
			parts.push(c.part);
		}
		allMarksOf.set(r.id, marks);
		const value = spec.rule === "MANUAL" ? (r.value ?? null) : judgedAggregate(spec, parts, scoredCodes);
		out.set(r.id, { id: r.id, parts, value, tieBreak: [] });
	});

	let ranks: Map<string, number | null>;
	if (spec.rule === "DANCE_SKATING") {
		ranks = danceRanks(spec, rows, inputs, out);
	} else {
		for (const r of rows) {
			const res = out.get(r.id)!;
			res.tieBreak = [...judgedTieBreak(spec.rule, res.parts, allMarksOf.get(r.id) ?? 0), tieOrderOf(r.detail)];
		}
		ranks = rankByValue(
			rows.map((r) => {
				const res = out.get(r.id)!;
				return { id: r.id, value: res.value, irm: r.irm, tieBreak: res.tieBreak };
			}),
			spec.lowerIsBetter,
		);
	}

	// Figure skating: each segment's place among the rows that skated it.
	if (spec.rule === "FIGURE_SKATING") {
		for (const def of spec.parts) {
			const seg = rankByValue(
				rows.flatMap((r) => {
					const p = out.get(r.id)!.parts.find((x) => x.code === def.code);
					return p ? [{ id: r.id, value: p.total, irm: r.irm, tieBreak: [neg(p.difficulty)] }] : [];
				}),
				false,
			);
			for (const r of rows) {
				const p = out.get(r.id)!.parts.find((x) => x.code === def.code);
				const rank = seg.get(r.id);
				if (p && rank != null) p.rank = rank;
			}
		}
	}

	let outcomes: Map<string, MeetOutcome | null> | null = null;
	if (bout) {
		const [a, b] = rows as [JudgedRowInput, JudgedRowInput];
		const [oa, ob] = judgedOutcomes({ irm: a.irm, rank: ranks.get(a.id) ?? null }, { irm: b.irm, rank: ranks.get(b.id) ?? null });
		outcomes = new Map([
			[a.id, oa],
			[b.id, ob],
		]);
	}
	return { rows: out, ranks, outcomes };
}

/** DANCE_SKATING: each dance's places across the couples, then the final placing (`skatingFinal`), then `tie_order`. */
function danceRanks(spec: JudgedRuleSpec, rows: readonly JudgedRowInput[], inputs: readonly Map<string, JudgedPartInput>[], out: Map<string, JudgedRowResult>): Map<string, number | null> {
	const g = spec.groups[0];
	const seats = g?.seats ?? [];
	const live = rows.map((r, i) => ({ r, input: inputs[i]! })).filter((x) => x.r.irm === null);
	const dancePlaces = new Map<string, number[]>();
	const allMarks = new Map<string, number[]>();
	let dances = 0;
	for (const def of spec.parts) {
		const marks = new Map<string, number[]>();
		for (const { r, input } of live) {
			const p = input.get(def.code);
			const m = p ? seats.flatMap((s) => (p.marks.has(s) ? [p.marks.get(s)!] : [])) : [];
			if (m.length) marks.set(r.id, m);
		}
		if (marks.size === 0) continue;
		dances++;
		const places = skatingPlaces(marks, seats.length);
		for (const [id, place] of places) {
			const part = out.get(id)?.parts.find((x) => x.code === def.code);
			if (part) part.total = place;
			dancePlaces.set(id, [...(dancePlaces.get(id) ?? []), place]);
			allMarks.set(id, [...(allMarks.get(id) ?? []), ...(marks.get(id) ?? [])]);
		}
	}
	for (const { r } of live) {
		const res = out.get(r.id)!;
		const places = dancePlaces.get(r.id);
		res.value = places ? round(sum(places), spec.decimals) : null;
	}
	const final = skatingFinal(dancePlaces, allMarks, seats.length, Math.max(1, dances));
	for (const r of rows) {
		const res = out.get(r.id)!;
		res.tieBreak = [tieOrderOf(r.detail)];
	}
	return rankByValue(
		rows.map((r) => ({ id: r.id, value: r.irm === null ? (final.get(r.id) ?? null) : null, irm: r.irm, tieBreak: out.get(r.id)!.tieBreak })),
		true,
	);
}

/** The result as the board shows it: the rule's decimals, always (9.850, 72.345). */
export function formatJudged(value: number, decimals: number): string {
	return formatDecimal(value, { minDecimals: decimals, maxDecimals: decimals });
}

/** The parts' detail entry for a row (`detail.parts`), with the stored `tie_order` and row `penalty` kept. */
export function judgedDetailOf(previous: unknown, parts: readonly JudgedPart[]): JudgedResultDetail {
	const out: JudgedResultDetail = {};
	if (parts.length) out.parts = [...parts];
	if (isObj(previous)) {
		if (isNum(previous.penalty)) out.penalty = previous.penalty;
		if (isNum(previous.tie_order)) out.tie_order = previous.tie_order;
	}
	return out;
}
